import { useState, useEffect } from "react";
import {
    AiOutlineFileText,
    AiOutlineSetting,
    AiOutlineBarChart,
    AiOutlineTag,
    AiOutlineCheck,
    AiOutlineClose,
    AiOutlineDollarCircle,
    AiOutlineWarning,
    AiOutlineInfoCircle,
    AiOutlineTool,
    AiOutlineIdcard,
    AiOutlineFileDone,
    AiOutlineShoppingCart,
    AiOutlineStop,
} from "react-icons/ai";
import { IoMdClose, IoIosSpeedometer } from "react-icons/io";
import { FaShieldAlt, FaGasPump, FaExclamationTriangle, FaSlidersH } from "react-icons/fa";
import GetColserAutoByPlacaApiImpl from "../../../../../../../data/repositories/colserauto/GetColserAutoByPlacaApiImpl";
import LoadingColserauto from "../PreviewImage/LoadingColserauto";
import "./styles.scss";

interface Props {
    placa: string;
    close: () => void;
}

const bool = (v: string | boolean) => v === true || v === "true";
const isNone = (v: any): boolean => v == null || v === "None" || v === "none" || v === "";
const isSi = (v: any): boolean =>
    v === true || v === "True" || v === "true" || v === "TRUE" || v === "SI" || v === "si";
const boolDisplay = (v: any): string => {
    if (isNone(v)) return "N/A";
    if (isSi(v)) return "Sí";
    if (v === false || v === "False" || v === "false" || v === "FALSE" || v === "NO" || v === "no") return "No";
    return String(v);
};
const naDisplay = (v: any): string => (isNone(v) ? "N/A" : String(v));
// Los identificadores (VIN, chasis, motor, serie) son información privada: solo se muestra el último dígito,
// igual que la placa.
const lastDigit = (v: any): string => (isNone(v) ? "N/A" : String(v).slice(-1));
const fmt = (n: number) =>
    new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 }).format(n);
const bCls = (p: number) => (p >= 90 ? "opt" : p >= 60 ? "ace" : "def");

const ZOOM_STEPS = [0.65, 0.75, 0.85, 1, 1.15, 1.3];

export default function ModalColserAutoPeritaje({ placa, close }: Props) {
    const [data, setData] = useState<any | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [zoom, setZoom] = useState(1);
    const [activeTab, setActiveTab] = useState<"inspeccion" | "avaluo" | "siniestros">("inspeccion");

    useEffect(() => {
        GetColserAutoByPlacaApiImpl(placa).then((res) => {
            if (res) {
                setData(res);
            } else {
                setError(true);
            }
            setLoading(false);
        });
    }, [placa]);

    const zoomIn = () =>
        setZoom((z) => {
            const i = ZOOM_STEPS.indexOf(z);
            return i < ZOOM_STEPS.length - 1 ? ZOOM_STEPS[i + 1] : z;
        });
    const zoomOut = () =>
        setZoom((z) => {
            const i = ZOOM_STEPS.indexOf(z);
            return i > 0 ? ZOOM_STEPS[i - 1] : z;
        });

    /* ── aliases (solo cuando data existe) ── */
    const d = data;
    const v = data?.vehiculo;
    const id = data?.identificadores;
    const sp = data?.especificaciones;
    const av = data?.avaluo;
    const en = data?.estados_normativos;
    const sn = data?.siniestros;

    const asegSi = d?.asegurable === "SI" || d?.asegurable === true || d?.asegurable === "true";
    // Solo consideramos sistemas con porcentaje numérico válido (el backend puede enviar
    // NaN/null cuando un sistema no se pudo evaluar, p. ej. "Aire Acondicionado").
    const validResultados: any[] = (d?.resultados_inspeccion ?? []).filter(
        (r: any) => typeof r.porcentaje === "number" && !isNaN(r.porcentaje),
    );
    const overallScore = validResultados.length
        ? Math.round(validResultados.reduce((acc: number, r: any) => acc + r.porcentaje, 0) / validResultados.length)
        : null;

    const especItems = sp
        ? [
              { name: "ABS", on: bool(sp.abs), extra: "" },
              { name: "Airbags", on: sp.airbags > 0, extra: `${sp.airbags} unid.` },
              { name: "Aire Acond.", on: bool(sp.aire_acondicionado), extra: "" },
              { name: "Vidrios Eléct.", on: sp.vidrios_electricos > 0, extra: `×${sp.vidrios_electricos}` },
              { name: "Espejos Eléct.", on: sp.espejos_electricos > 0, extra: `×${sp.espejos_electricos}` },
              { name: "Sensores", on: bool(sp.sensores), extra: "" },
              { name: "Cámara Reversa", on: bool(sp.camara_reversa), extra: "" },
              { name: "Sunroof", on: bool(sp.sunroof), extra: "" },
              { name: "Exploradoras", on: bool(sp.exploradoras), extra: "" },
              {
                  name: "Sillas Eléct.",
                  on: sp.sillasElectricas > 0,
                  extra: sp.sillasElectricas > 0 ? `×${sp.sillasElectricas}` : "",
              },
              { name: `Faros ${sp.tipo_faros}`, on: true, extra: "" },
              { name: "Tapicería Cuero", on: bool(sp.tapiceriaCuero), extra: "" },
          ]
        : [];

    return (
        <div className="per-overlay" onClick={(e) => e.target === e.currentTarget && close()}>
            {/* ── TOOLBAR ── */}
            {!error && (
                <div className="per-toolbar">
                    <div className="per-tb-left">
                        <span className="per-tb-title">Informe de Peritaje</span>
                        {d && (
                            <span className="per-tb-sub">
                                No. {d.numero_inspeccion} · {d.fecha_inspeccion}
                            </span>
                        )}
                    </div>
                    <div className="per-tb-right">
                        <div className="per-zoom-group">
                            <button className="per-zoom-btn" onClick={zoomOut} disabled={zoom === ZOOM_STEPS[0]}>
                                −
                            </button>
                            <span className="per-zoom-label">{Math.round(zoom * 100)}%</span>
                            <button
                                className="per-zoom-btn"
                                onClick={zoomIn}
                                disabled={zoom === ZOOM_STEPS[ZOOM_STEPS.length - 1]}
                            >
                                +
                            </button>
                        </div>
                        <button className="per-close-btn" onClick={close}>
                            <IoMdClose size={20} />
                        </button>
                    </div>
                </div>
            )}

            {/* ── ESTADOS: loading / error ── */}
            {loading && <LoadingColserauto isDone={false} />}

            {error && !loading && (
                <div className="per-state-screen per-state-error">
                    <img
                        src="/assets/not-found-peritaje.png"
                        alt="No se encontró información de peritaje"
                        className="per-error-img"
                    />
                    <button onClick={close}>Cerrar</button>
                </div>
            )}

            {/* ── DOCUMENT ── */}
            {!loading && !error && d && (
                <div className="per-doc-wrap" style={{ zoom }}>
                    <div className="per-page">
                        {/* ══ HEADER ══ */}
                        <div className="per-header">
                            <div className="per-hdr-left">
                                <div className="per-hdr-divider" />
                                <div className="per-renting">
                                    <img
                                        src="/assets/icons/featureList/colserauto-pdf.png"
                                        alt="ColserAuto"
                                        className="per-colserauto-logo"
                                    />
                                </div>
                            </div>
                            <div className="per-hdr-right">
                                <img src="/assets/logos/horizontal.svg" alt="WCAR" className="per-wcar-logo" />
                            </div>
                        </div>

                        {/* ══ HERO ══ */}
                        <div className="per-hero">
                            <div className="per-hero-left">
                                <div className="per-eyebrow-row">
                                    <div className="per-hero-eyebrow">
                                        <AiOutlineFileDone size={13} />
                                        {d.Paquete_servcio}
                                        <span className="per-eyebrow-sep" />
                                        <span className="per-eyebrow-date">{d.fecha_inspeccion}</span>
                                    </div>
                                    <div className={`per-aseg-badge ${asegSi ? "aseg-si" : "aseg-no"}`}>
                                        <FaShieldAlt size={11} />
                                        <span>
                                            Asegurable: <strong>{asegSi ? "Sí" : "No"}</strong>
                                        </span>
                                    </div>
                                </div>
                                <div className="per-hero-title">
                                    PERITAJE
                                    <br />
                                    COMERCIAL
                                </div>
                                <div className="per-meta-grid">
                                    {[
                                        { lbl: "No. Inspección", val: String(d.numero_inspeccion) },
                                        { lbl: "Clase", val: v.clase },
                                        { lbl: "Carrocería", val: v.carroceria },
                                        { lbl: "Transmisión", val: sp.transmision },
                                        { lbl: "Tracción", val: sp.traccion },
                                    ].map(({ lbl, val }) => (
                                        <div className="per-meta-cell" key={lbl}>
                                            <span className="pmc-lbl">{lbl}</span>
                                            <span className="pmc-val">{val}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="per-hero-badges">
                                <div className="per-placa-wrap">
                                    <span className="per-placa-hint">PLACA</span>
                                    <div className="per-placa">{v.placa.slice(-1)}</div>
                                </div>
                                {overallScore !== null && (
                                    <div className={`per-score-card per-score-${bCls(overallScore)}`}>
                                        <span className="psc-num">{overallScore}%</span>
                                        <span className="psc-lbl">Puntuación general</span>
                                    </div>
                                )}
                            </div>

                            <div className="per-vehicle-card">
                                <div className="pvc-header">
                                    {v.marca} · {v.linea} · {v.modelo}
                                </div>
                                <div className="pvc-grid">
                                    {[
                                        ["Referencia", `${v.referencia_1} ${v.referencia_2}`],
                                        ["Color", v.color],
                                        ["Kilometraje", `${v.kilometraje.toLocaleString("es-CO")} km`],
                                        ["Cilindraje", `${v.cilindraje.toLocaleString("es-CO")} cc`],
                                        ["Combustible", v.combustible],
                                        ["Tipo Caja", v.tipo_caja],
                                    ].map(([l, val]) => (
                                        <div className="pvc-item" key={l}>
                                            <span className="pvc-lbl">{l}</span>
                                            <span className="pvc-val">{val}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* ══ IDENTIFIERS ══ */}
                        <div className="per-ids">
                            {[
                                { icon: <AiOutlineIdcard size={20} />, lbl: "VIN / Chasis", val: lastDigit(id.vin) },
                                {
                                    icon: <AiOutlineFileText size={20} />,
                                    lbl: "No. Chasis",
                                    val: lastDigit(id.numero_chasis),
                                },
                                {
                                    icon: <AiOutlineTool size={20} />,
                                    lbl: "No. Motor",
                                    val: lastDigit(id.numero_motor),
                                },
                                {
                                    icon: <AiOutlineTag size={20} />,
                                    lbl: "No. Serie",
                                    val: lastDigit(id.numero_serie),
                                },
                            ].map((c) => (
                                <div className="per-id-card" key={c.lbl}>
                                    <div className="per-id-icon">{c.icon}</div>
                                    <div className="per-id-info">
                                        <span className="per-id-lbl">{c.lbl}</span>
                                        <span className="per-id-val">{c.val}</span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* ══ SPECS BAR ══ */}
                        <div className="per-specs-bar">
                            {[
                                { icon: <AiOutlineSetting size={18} />, lbl: "Tipo de Caja", val: v.tipo_caja },
                                { icon: <FaGasPump size={16} />, lbl: "Combustible", val: v.combustible },
                                {
                                    icon: <IoIosSpeedometer size={18} />,
                                    lbl: "Cilindraje",
                                    val: `${v.cilindraje.toLocaleString("es-CO")} cc`,
                                },
                                { icon: <AiOutlineStop size={18} />, lbl: "Frenos", val: sp.frenos },
                                { icon: <FaSlidersH size={16} />, lbl: "Dirección", val: sp.tipo_direccion },
                            ].map((c) => (
                                <div className="per-spec-pill" key={c.lbl}>
                                    <div className="psp-icon">{c.icon}</div>
                                    <div>
                                        <span className="psp-lbl">{c.lbl}</span>
                                        <span className="psp-val">{c.val}</span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* ══ TABS ══ */}
                        <div className="per-tabs">
                            {(
                                [
                                    {
                                        id: "inspeccion",
                                        icon: <AiOutlineBarChart size={15} />,
                                        label: "Inspección & Especificaciones",
                                    },
                                    {
                                        id: "avaluo",
                                        icon: <AiOutlineDollarCircle size={15} />,
                                        label: "Avalúo & Fasecolda",
                                    },
                                    {
                                        id: "siniestros",
                                        icon: <FaExclamationTriangle size={13} />,
                                        label: "Siniestros & Antecedentes",
                                    },
                                ] as const
                            ).map((tab) => (
                                <button
                                    key={tab.id}
                                    className={`per-tab ${activeTab === tab.id ? "per-tab-active" : ""}`}
                                    onClick={() => setActiveTab(tab.id)}
                                >
                                    {tab.icon} {tab.label}
                                </button>
                            ))}
                        </div>

                        {/* ══ TAB: INSPECCIÓN ══ */}
                        {activeTab === "inspeccion" && (
                            <div className="per-content-grid">
                                <div className="per-col-main">
                                    <div className="per-section-card">
                                        <div className="per-sc-head">
                                            <div className="per-sc-icon navy">
                                                <AiOutlineBarChart size={16} />
                                            </div>
                                            <span>Resultados de Inspección</span>
                                            <div className="per-legend">
                                                {[
                                                    { c: "var(--p-red)", l: "0–59% Deficiente" },
                                                    { c: "var(--p-yellow)", l: "60–89% Aceptable" },
                                                    { c: "var(--p-green)", l: "90–100% Óptimo" },
                                                ].map((x) => (
                                                    <span key={x.l} className="per-legend-item">
                                                        <span style={{ background: x.c }} className="per-legend-dot" />
                                                        {x.l}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="per-bars">
                                            {d.resultados_inspeccion.map((r: any) => {
                                                const hasPct = typeof r.porcentaje === "number" && !isNaN(r.porcentaje);
                                                const pct = hasPct ? r.porcentaje : 0;
                                                return (
                                                    <div className="per-bar-row" key={r.sistema}>
                                                        <span className="per-bar-lbl">{r.sistema}</span>
                                                        <div className="per-bar-track">
                                                            <div
                                                                className={`per-bar-fill per-fill-${bCls(pct)}`}
                                                                style={{ width: `${pct}%` }}
                                                            />
                                                        </div>
                                                        <span className={`per-bar-pct per-pct-${bCls(pct)}`}>
                                                            {hasPct ? `${r.porcentaje}%` : "N/A"}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div className="per-section-card">
                                        <div className="per-sc-head">
                                            <div className="per-sc-icon blue">
                                                <AiOutlineSetting size={16} />
                                            </div>
                                            <span>Especificaciones Técnicas</span>
                                        </div>
                                        <div className="per-espec-grid">
                                            {especItems.map((e) => (
                                                <div
                                                    className={`per-espec-pill ${e.on ? "ep-on" : "ep-off"}`}
                                                    key={e.name}
                                                >
                                                    <span className="ep-dot">
                                                        {e.on ? (
                                                            <AiOutlineCheck size={13} />
                                                        ) : (
                                                            <AiOutlineClose size={13} />
                                                        )}
                                                    </span>
                                                    <span className="ep-name">{e.name}</span>
                                                    {e.extra && <span className="ep-extra">{e.extra}</span>}
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="per-section-card">
                                        <div className="per-sc-head">
                                            <div className="per-sc-icon orange">
                                                <AiOutlineShoppingCart size={16} />
                                            </div>
                                            <span>Accesorios</span>
                                        </div>
                                        <table className="per-tbl">
                                            <thead>
                                                <tr>
                                                    <th>Accesorio</th>
                                                    <th>Existencia</th>
                                                    <th>Valor</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {d.accesorios.map((a: any, i: number) => (
                                                    <tr key={i}>
                                                        <td>{a.nombre}</td>
                                                        <td>
                                                            <span
                                                                className={`per-chip ${isSi(a.existencia) ? "chip-green" : "chip-gray"}`}
                                                            >
                                                                {boolDisplay(a.existencia)}
                                                            </span>
                                                        </td>
                                                        <td className="tbl-mono">{fmt(a.valor)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <div className="per-col-side">
                                    <div className="per-section-card">
                                        <div className="per-sc-head">
                                            <div className="per-sc-icon navy">
                                                <FaShieldAlt size={14} />
                                            </div>
                                            <span>Estados Normativos</span>
                                        </div>
                                        <div className="per-norm-grid">
                                            {[
                                                {
                                                    tipo: "SOAT",
                                                    ok: bool(en.soat_vigente),
                                                    fecha: en.fecha_vencimiento_soat,
                                                },
                                                {
                                                    tipo: "RTM",
                                                    ok: bool(en.rtm_vigente),
                                                    fecha: en.fecha_vencimiento_rtm,
                                                },
                                            ].map((n) => (
                                                <div
                                                    className={`per-norm-card ${n.ok ? "nc-ok" : "nc-fail"}`}
                                                    key={n.tipo}
                                                >
                                                    <div className="pnc-tipo">{n.tipo}</div>
                                                    <div className="pnc-status">
                                                        {n.ok ? (
                                                            <>
                                                                <AiOutlineCheck size={12} /> VIGENTE
                                                            </>
                                                        ) : (
                                                            <>
                                                                <AiOutlineClose size={12} /> VENCIDO
                                                            </>
                                                        )}
                                                    </div>
                                                    <div className="pnc-fecha">Vence: {n.fecha}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="per-section-card">
                                        <div className="per-sc-head">
                                            <div className="per-sc-icon navy">
                                                <AiOutlineFileDone size={16} />
                                            </div>
                                            <span>Fasecolda</span>
                                        </div>
                                        <div className="per-kv-list">
                                            <div className="per-kv">
                                                <span>Código</span>
                                                <strong>{d.fasecolda.codigo_fasecolda}</strong>
                                            </div>
                                            <div className="per-kv">
                                                <span>Nac.</span>
                                                <strong>{d.fasecolda.nacionalidad}</strong>
                                            </div>
                                            <div className="per-kv-full">{d.fasecolda.descripcion_fasecolda}</div>
                                            <div className="per-kv-highlight">
                                                <span>Valor</span>
                                                <strong>{fmt(d.fasecolda.valor_fasecolda)}</strong>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ══ TAB: AVALÚO ══ */}
                        {activeTab === "avaluo" && (
                            <div className="per-content-grid">
                                <div className="per-col-main">
                                    <div className="per-section-card">
                                        <div className="per-sc-head">
                                            <div className="per-sc-icon orange">
                                                <AiOutlineDollarCircle size={16} />
                                            </div>
                                            <span>Avalúo Comercial</span>
                                        </div>
                                        <div className="per-avaluo-breakdown">
                                            <div className="pab-row">
                                                <span>Valor Comercial</span>
                                                <strong>{fmt(av.valor_comercial)}</strong>
                                            </div>
                                            <div className="pab-row pab-minus">
                                                <span>Demérito Latonería / Pintura</span>
                                                <strong>− {fmt(av.demerito_latoneria_pintura)}</strong>
                                            </div>
                                            <div className="pab-row pab-minus">
                                                <span>Demérito Otros</span>
                                                <strong>− {fmt(av.demerito_otros)}</strong>
                                            </div>
                                            <div className="pab-divider" />
                                            <div className="pab-total">
                                                <span>Valor Final</span>
                                                <strong>{fmt(av.valor_final)}</strong>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="per-section-card">
                                        <div className="per-sc-head">
                                            <div className="per-sc-icon navy">
                                                <AiOutlineFileDone size={16} />
                                            </div>
                                            <span>Datos Fasecolda</span>
                                        </div>
                                        <div className="per-fasecolda-full">
                                            {[
                                                ["Código Fasecolda", d.fasecolda.codigo_fasecolda],
                                                ["Descripción", d.fasecolda.descripcion_fasecolda],
                                                ["Nacionalidad", d.fasecolda.nacionalidad],
                                                ["Valor Fasecolda", fmt(d.fasecolda.valor_fasecolda)],
                                            ].map(([l, val]) => (
                                                <div
                                                    className={`per-kv per-kv-lg ${l === "Descripción" ? "per-kv-full" : ""}`}
                                                    key={l}
                                                >
                                                    <span>{l}</span>
                                                    <strong className={l === "Valor Fasecolda" ? "per-kv-orange" : ""}>
                                                        {val}
                                                    </strong>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="per-col-side">
                                    <div className="per-section-card">
                                        <div className="per-sc-head">
                                            <div className="per-sc-icon navy">
                                                <FaShieldAlt size={14} />
                                            </div>
                                            <span>Estados Normativos</span>
                                        </div>
                                        <div className="per-norm-grid">
                                            {[
                                                {
                                                    tipo: "SOAT",
                                                    ok: bool(en.soat_vigente),
                                                    fecha: en.fecha_vencimiento_soat,
                                                },
                                                {
                                                    tipo: "RTM",
                                                    ok: bool(en.rtm_vigente),
                                                    fecha: en.fecha_vencimiento_rtm,
                                                },
                                            ].map((n) => (
                                                <div
                                                    className={`per-norm-card ${n.ok ? "nc-ok" : "nc-fail"}`}
                                                    key={n.tipo}
                                                >
                                                    <div className="pnc-tipo">{n.tipo}</div>
                                                    <div className="pnc-status">
                                                        {n.ok ? (
                                                            <>
                                                                <AiOutlineCheck size={12} /> VIGENTE
                                                            </>
                                                        ) : (
                                                            <>
                                                                <AiOutlineClose size={12} /> VENCIDO
                                                            </>
                                                        )}
                                                    </div>
                                                    <div className="pnc-fecha">Vence: {n.fecha}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ══ TAB: SINIESTROS ══ */}
                        {activeTab === "siniestros" && (
                            <div className="per-content-grid">
                                <div className="per-col-main">
                                    <div className="per-section-card">
                                        <div className="per-sc-head">
                                            <div className="per-sc-icon red">
                                                <FaExclamationTriangle size={14} />
                                            </div>
                                            <span>Siniestros</span>
                                        </div>
                                        <div className="per-sin-summary">
                                            {[
                                                {
                                                    l: "Vigencia Póliza",
                                                    v: naDisplay(sn.vigencia_poliza),
                                                    ok: sn.vigencia_poliza === "VIGENTE",
                                                },
                                                {
                                                    l: "Consulta Siniestro",
                                                    v: boolDisplay(sn.consulta_siniestro),
                                                    ok: !isSi(sn.consulta_siniestro),
                                                },
                                                {
                                                    l: "Presenta Recl.",
                                                    v: boolDisplay(sn.presenta_reclamacion),
                                                    ok: !isSi(sn.presenta_reclamacion),
                                                },
                                            ].map((x) => (
                                                <div
                                                    className={`per-sin-card ${x.ok ? "sin-ok" : "sin-alert"}`}
                                                    key={x.l}
                                                >
                                                    <span className="psc-lbl">{x.l}</span>
                                                    <span className="psc-val">{x.v}</span>
                                                </div>
                                            ))}
                                        </div>
                                        {sn.detalle?.length > 0 && (
                                            <>
                                                <table className="per-tbl" style={{ marginTop: 10 }}>
                                                    <thead>
                                                        <tr>
                                                            <th>Tipo</th>
                                                            <th>Fecha</th>
                                                            <th>Valor</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {sn.detalle.map((s: any, i: number) => (
                                                            <tr key={i}>
                                                                <td>
                                                                    <span className="per-chip chip-red">
                                                                        {naDisplay(s.tipo_reclamacion)}
                                                                    </span>
                                                                </td>
                                                                <td>{naDisplay(s.fecha)}</td>
                                                                <td className="tbl-mono">
                                                                    {isNone(s.valor) ? "N/A" : fmt(s.valor)}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                                {sn.detalle.map((s: any, i: number) => (
                                                    <div className="per-obs" key={i}>
                                                        <AiOutlineWarning size={13} /> <strong>Observación:</strong>{" "}
                                                        {naDisplay(s.observacion)}
                                                    </div>
                                                ))}
                                            </>
                                        )}
                                    </div>
                                </div>

                                <div className="per-col-side">
                                    <div className="per-section-card">
                                        <div className="per-sc-head">
                                            <div className="per-sc-icon yellow">
                                                <AiOutlineInfoCircle size={16} />
                                            </div>
                                            <span>Antecedentes</span>
                                        </div>
                                        <table className="per-tbl">
                                            <thead>
                                                <tr>
                                                    <th>Código</th>
                                                    <th>Descripción</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {!d.antecedentes?.length ? (
                                                    <tr>
                                                        <td colSpan={2} className="tbl-empty">
                                                            Sin antecedentes
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    d.antecedentes.map((a: any, i: number) => (
                                                        <tr key={i}>
                                                            <td className="tbl-code">{naDisplay(a.codigo_novedad)}</td>
                                                            <td>{naDisplay(a.descripcion_novedad)}</td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ══ DISCLAIMER ══ */}
                        <div className="per-disclaimer">
                            <div className="per-disc-strip">
                                {[
                                    "Este documento es un resumen del informe de peritaje. No genera efecto vinculante para la comercialización del vehículo.",
                                    "Colserauto aplicó criterios técnicos para la revisión. El informe completo se encuentra en el documento adjunto.",
                                    "Se sugiere solicitar el Certificado de Tradición para descartar multas, embargos y otras alertas.",
                                ].map((t, i) => (
                                    <div className="per-disc-item" key={i}>
                                        <div className="per-disc-num">{i + 1}</div>
                                        <p>{t}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
